import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './AgentVersionDetails.css';
import './CurrentAgentControl.css';

function AgentVersionDetails({ user }) {
  const { agentId } = useParams();
  const [versions, setVersions] = useState([]);
  const [currentVersionIndex, setCurrentVersionIndex] = useState(0);
  const [currentVersionDetails, setCurrentVersionDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isCurrentMyJavaAgent, setIsCurrentMyJavaAgent] = useState(false);
  const [isSettingCurrentAgent, setIsSettingCurrentAgent] = useState(false);
  const [clientEvents, setClientEvents] = useState({
    audio: false,
    interruption: false,
    user_transcript: false,
    agent_response: false,
    agent_response_correction: false
  });
  
  // Fetch the list of versions (without large text fields)
  useEffect(() => {
    const fetchVersions = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`/api/admin/elevenlabs-agents/${agentId}/history`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch agent versions');
        }
        
        const data = await response.json();
        setVersions(data);
        setError('');
        
        // After fetching versions, load details for the first version
        if (data.length > 0) {
          fetchVersionDetails(data[0].id);
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Error fetching agent versions:", err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    fetchVersions();
    checkCurrentAgentStatus();
  }, [agentId]);
  
  // Check if this agent is the current MyJava agent
  const checkCurrentAgentStatus = async () => {
    try {
      const response = await fetch(`/api/admin/elevenlabs-current-agent/${agentId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to check current agent status');
      }
      
      const data = await response.json();
      setIsCurrentMyJavaAgent(data.isCurrent);
    } catch (err) {
      console.error("Error checking current agent status:", err);
      // Don't set error state to avoid interfering with other operations
    }
  };
  
  // Set this agent as the current MyJava agent
  const setAsCurrentAgent = async () => {
    try {
      setIsSettingCurrentAgent(true);
      setError('');
      setSuccessMessage('');
      
      const response = await fetch('/api/admin/elevenlabs-current-agent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ agentId }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to set as current agent');
      }
      
      setIsCurrentMyJavaAgent(true);
      setSuccessMessage('Agent set as current MyJava agent successfully');
    } catch (err) {
      console.error("Error setting current agent:", err);
      setError(`Failed to set as current agent: ${err.message}`);
    } finally {
      setIsSettingCurrentAgent(false);
    }
  };
  
  // Fetch complete details for a specific version
  const fetchVersionDetails = async (versionId) => {
    try {
      setIsLoadingDetails(true);
      const response = await fetch(`/api/admin/elevenlabs-agents/version/${versionId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch version details');
      }
      
      const data = await response.json();
      setCurrentVersionDetails(data);
      
      // Extract client events from database fields
      setClientEvents({
        audio: !!data.client_event_audio,
        interruption: !!data.client_event_interruption,
        user_transcript: !!data.client_event_user_transcript,
        agent_response: !!data.client_event_agent_response,
        agent_response_correction: !!data.client_event_agent_response_correction
      });
      
    } catch (err) {
      console.error("Error fetching version details:", err);
      setError(`Error loading version details: ${err.message}`);
    } finally {
      setIsLoadingDetails(false);
      setIsLoading(false);
    }
  };

  // Format date to ISO 8601 without timezone
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toISOString().slice(0, 19).replace('T', ' ');
  };

  // Go to previous version
  const prevVersion = () => {
    if (currentVersionIndex < versions.length - 1) {
      const newIndex = currentVersionIndex + 1;
      setCurrentVersionIndex(newIndex);
      fetchVersionDetails(versions[newIndex].id);
    }
  };

  // Go to next version
  const nextVersion = () => {
    if (currentVersionIndex > 0) {
      const newIndex = currentVersionIndex - 1;
      setCurrentVersionIndex(newIndex);
      fetchVersionDetails(versions[newIndex].id);
    }
  };

  // Safe text getter
  const getSafeText = (obj, key) => {
    if (!obj) return 'N/A';
    const value = obj[key];
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  // Download raw data
  const downloadRawData = () => {
    if (!currentVersionDetails) {
      setError('No version details available');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      // Get raw data - handle different possible formats
      let rawData;
      
      if (!currentVersionDetails.raw_data) {
        setError('No raw data available for this version');
        setTimeout(() => setError(''), 3000);
        return;
      }
      
      // Check if raw_data is already a string
      if (typeof currentVersionDetails.raw_data === 'string') {
        rawData = currentVersionDetails.raw_data;
      } 
      // Check if it's an object that needs to be stringified
      else if (typeof currentVersionDetails.raw_data === 'object') {
        rawData = JSON.stringify(currentVersionDetails.raw_data, null, 2);
      } else {
        // Handle other cases
        rawData = String(currentVersionDetails.raw_data);
      }
      
      // Create a blob with the raw data
      const blob = new Blob([rawData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentVersionDetails.name || 'agent'}_${currentVersionDetails.agent_id}_v${currentVersionDetails.id}.json`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      // Show success message
      setSuccessMessage('Raw data downloaded successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error downloading raw data:', err);
      setError(`Failed to download raw data: ${err.message}`);
      setTimeout(() => setError(''), 3000);
    }
  };

  // If not admin, redirect
  if (user.role !== 'admin') {
    return (
      <div className="admin-container">
        <h2>Access Denied</h2>
        <p>You do not have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="agent-header">
        <h2>Agent Version Details</h2>
        <div>
          <Link to="/admin?tab=elevenlabs" className="back-button">ElevenLabs Agents</Link>
          <a 
            href={`https://elevenlabs.io/app/conversational-ai/agents/${agentId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="elevenlabs-button"
          >
            View on ElevenLabs
          </a>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      {isLoading ? (
        <p>Loading agent details...</p>
      ) : versions.length === 0 ? (
        <p>No version history found for this agent.</p>
      ) : (
        <div>
          {versions.length > 1 && (
            <div className="version-navigation">
              <button 
                onClick={prevVersion} 
                disabled={currentVersionIndex >= versions.length - 1}
                className="nav-button"
              >
                &laquo; Older
              </button>
              <span className="version-indicator">
                Version {versions.length - currentVersionIndex} of {versions.length}
              </span>
              <button 
                onClick={nextVersion} 
                disabled={currentVersionIndex <= 0}
                className="nav-button"
              >
                Newer &raquo;
              </button>
            </div>
          )}
          
          {isLoadingDetails ? (
            <p>Loading version details...</p>
          ) : currentVersionDetails && (
            <div className="agent-details">
              <h3>Version {currentVersionDetails.id} Details</h3>
              
              <div className="action-buttons">
                <div className="left-buttons">
                  <button 
                    className="action-button download-button"
                    onClick={downloadRawData}
                  >
                    Download Raw Data
                  </button>
                </div>
                
                <div className="right-buttons">
                  {isCurrentMyJavaAgent ? (
                    <div className="current-agent-indicator">
                      Current MyJava Agent
                    </div>
                  ) : (
                    <button 
                      className="action-button enable-button"
                      onClick={setAsCurrentAgent}
                      disabled={isSettingCurrentAgent}
                    >
                      {isSettingCurrentAgent ? 'Setting...' : 'Enable as Current MyJava Agent'}
                    </button>
                  )}
                </div>
              </div>
              
              <table className="details-table">
                <tbody>
                  <tr className="category-header">
                    <th>Basic Info</th>
                    <td colSpan="3"></td>
                  </tr>
                  <tr>
                    <th>Agent ID</th>
                    <td>{currentVersionDetails.agent_id}</td>
                    <th>Name</th>
                    <td>{currentVersionDetails.name}</td>
                  </tr>
                  <tr>
                    <th>Created At</th>
                    <td>{formatDate(currentVersionDetails.created_at)}</td>
                    <th>Captured At</th>
                    <td>{formatDate(currentVersionDetails.captured_at)}</td>
                  </tr>
                  <tr>
                    <th>Language</th>
                    <td colSpan="3">{currentVersionDetails.language || 'N/A'}</td>
                  </tr>
                  
                  <tr className="category-header">
                    <th>LLM Config</th>
                    <td colSpan="3"></td>
                  </tr>
                  <tr>
                    <th>LLM Model</th>
                    <td>{currentVersionDetails.llm_model || 'N/A'}</td>
                    <th>Temperature</th>
                    <td>{currentVersionDetails.temperature !== null ? currentVersionDetails.temperature : 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Max Tokens</th>
                    <td>{currentVersionDetails.max_tokens !== null ? currentVersionDetails.max_tokens : 'N/A'}</td>
                    <th>Max Duration</th>
                    <td>{currentVersionDetails.max_duration_seconds || 'N/A'} seconds</td>
                  </tr>
                  
                  <tr className="category-header">
                    <th>Voice Config</th>
                    <td colSpan="3"></td>
                  </tr>
                  <tr>
                    <th>Voice ID</th>
                    <td>{currentVersionDetails.voice_id || 'N/A'}</td>
                    <th>Voice Model</th>
                    <td>{currentVersionDetails.voice_model_id || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Voice Stability</th>
                    <td>{currentVersionDetails.voice_stability !== null ? currentVersionDetails.voice_stability : 'N/A'}</td>
                    <th>Voice Similarity Boost</th>
                    <td>{currentVersionDetails.voice_similarity_boost !== null ? currentVersionDetails.voice_similarity_boost : 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Voice Speed</th>
                    <td>{currentVersionDetails.voice_speed !== null ? currentVersionDetails.voice_speed : 'N/A'}</td>
                    <th>Optimize Streaming</th>
                    <td>{currentVersionDetails.optimize_streaming_latency !== null ? currentVersionDetails.optimize_streaming_latency : 'N/A'}</td>
                  </tr>
                  
                  <tr className="category-header">
                    <th>Audio Config</th>
                    <td colSpan="3"></td>
                  </tr>
                  <tr>
                    <th>Output Format</th>
                    <td>{currentVersionDetails.agent_output_audio_format || 'N/A'}</td>
                    <th>Input Format</th>
                    <td>{currentVersionDetails.user_input_audio_format || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>ASR Quality</th>
                    <td>{currentVersionDetails.asr_quality || 'N/A'}</td>
                    <th>ASR Provider</th>
                    <td>{currentVersionDetails.asr_provider || 'N/A'}</td>
                  </tr>
                  
                  <tr className="category-header">
                    <th>Conversation</th>
                    <td colSpan="3"></td>
                  </tr>
                  <tr>
                    <th>Turn Mode</th>
                    <td>{currentVersionDetails.turn_mode || 'N/A'}</td>
                    <th>Turn Timeout</th>
                    <td>{currentVersionDetails.turn_timeout !== null ? currentVersionDetails.turn_timeout : 'N/A'} seconds</td>
                  </tr>
                  <tr>
                    <th>Silence End Call</th>
                    <td colSpan="3">{currentVersionDetails.silence_end_call_timeout !== null ? currentVersionDetails.silence_end_call_timeout : 'N/A'} seconds</td>
                  </tr>
                  
                  <tr className="category-header">
                    <th>Override Settings</th>
                    <td colSpan="3"></td>
                  </tr>
                  <tr>
                    <th>Agent Language</th>
                    <td>{currentVersionDetails.override_agent_language !== null ? 
                        (currentVersionDetails.override_agent_language ? 'Allowed' : 'Not Allowed') : 'N/A'}</td>
                    <th>First Message</th>
                    <td>{currentVersionDetails.override_first_message !== null ? 
                        (currentVersionDetails.override_first_message ? 'Allowed' : 'Not Allowed') : 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>System Prompt</th>
                    <td>{currentVersionDetails.override_system_prompt !== null ? 
                        (currentVersionDetails.override_system_prompt ? 'Allowed' : 'Not Allowed') : 'N/A'}</td>
                    <th>Voice</th>
                    <td>{currentVersionDetails.override_voice !== null ? 
                        (currentVersionDetails.override_voice ? 'Allowed' : 'Not Allowed') : 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Webhook Initiation</th>
                    <td colSpan="3">{currentVersionDetails.enable_webhook_initiation !== null ? 
                        (currentVersionDetails.enable_webhook_initiation ? 'Enabled' : 'Disabled') : 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
              
              <div className="text-fields">
                <div className="text-field">
                  <h4>First Message</h4>
                  <pre className="text-content">
                    {getSafeText(currentVersionDetails, 'first_message')}
                  </pre>
                </div>
                
                <div className="text-field">
                  <h4>System Prompt</h4>
                  <pre className="text-content">
                    {getSafeText(currentVersionDetails, 'system_prompt')}
                  </pre>
                </div>
              </div>
              
              <div className="client-events-section">
                <h3>Client Events</h3>
                <div className="client-events-grid">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={clientEvents.audio}
                      disabled={true}
                    />
                    <span>Audio</span>
                  </label>
                  
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={clientEvents.interruption}
                      disabled={true}
                    />
                    <span>Interruption</span>
                  </label>
                  
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={clientEvents.user_transcript}
                      disabled={true}
                    />
                    <span>User Transcript</span>
                  </label>
                  
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={clientEvents.agent_response}
                      disabled={true}
                    />
                    <span>Agent Response</span>
                  </label>
                  
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={clientEvents.agent_response_correction}
                      disabled={true}
                    />
                    <span>Agent Response Correction</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AgentVersionDetails;